import { combineEpics, Epic, ofType, StateObservable } from 'redux-observable';
import { iif, Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  first,
  map,
  mergeMap,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { requestActions } from './apiHelpers';
import { AppState } from '@reducers/rootReducer';
import {
  ID,
  ReduxAction,
  SearchSort,
  SearchSortBy,
  SearchSortByField,
  SearchSortDirection,
  SearchState,
  SearchType,
  UserSession,
} from '../types';
import { Action } from 'redux';
import { searchApiObservable } from '@support/api';
import { AjaxResponse } from 'rxjs/ajax';
import { selectUser } from '@reducers/user/account';
import { selectGroup } from './groups';
import { selectAllContacts } from './contacts';

/*
We differentiate between the primary search that is controlling the /search view and primary search
input (searchActions) and any other search-based contact display in the app. The primary search includes some
additional functionality around saving/clearing search history and fetching search "tips." All other
search state is tied to one of three slices:
	allPeopleSearch -> view of all of a user's contacts in the /people view. This slice uses pagination and sorting
	adHocSearch -> any generic contact search (e.g. search in Command Bar or in Merge Contacts modal). No pagination or sorting
	mentionSearch -> search specific to note mentions. No pagination or sorting. We use this slice as opposed to adHocSearch
  	because we can have results from mentions and adHocSearch rendered in the same view.

New search slices and related functionality can be created using createSearchActions (below) and
createSearchReducer (in reducers/data/search/createSearchReducer.ts).
*/

export const identifierForSearchType: {
  [k in Exclude<SearchType, 'search'>]: 'AD_HOC' | 'MENTION' | '_ALL_PEOPLE' | 'GROUP';
} = {
  adHocSearch: 'AD_HOC',
  mentionSearch: 'MENTION',
  allPeopleSearch: '_ALL_PEOPLE',
  groupSearch: 'GROUP',
};

function createSearchActions(
  searchType: SearchType,
  opts: { debounce: boolean } = { debounce: true },
): [
  any,
  {
    setTerm: (
      term: string,
      resultsCount: number,
      searchAttributes: Array<string>,
      useMainSearchSorting?: boolean,
    ) => Action;
    clear: () => Action;
    fetchMore: (resultsCount?: number) => Action;
    refetchCurrentPage: () => Action;
    sort: (type: SearchSortBy, direction: SearchSortDirection) => Action;
  },
  Epic,
  Epic,
] {
  const identifier = identifierForSearchType[searchType];
  const searchActions = requestActions(`${identifier}_SEARCH_RESULTS`, ['fetch']);
  const SET_SEARCH_TERM = `SET_${identifier}_SEARCH_TERM`;
  const CLEAR_SEARCH_TERM = `CLEAR_${identifier}_SEARCH_TERM`;
  const FETCH_MORE_SEARCH_RESULTS = `FETCH_MORE_${identifier}_SEARCH_RESULTS`;
  const REFETCH_SEARCH_RESULTS_PAGE = `REFETCH_${identifier}_SEARCH_RESULTS_PAGE`;
  const SET_SEARCH_SORTING = `SET_${identifier}_SEARCH_SORTING`;

  const setTerm = (
    term: string,
    resultsCount: number,
    searchAttributes: Array<string>,
    useMainSearchSorting?: boolean,
  ) => ({
    type: SET_SEARCH_TERM,
    payload: { term, hitsPerPage: resultsCount, searchAttributes, useMainSearchSorting },
  });
  const sort = (type: SearchSortBy, direction: SearchSortDirection) => ({
    type: SET_SEARCH_SORTING,
    payload: { type, direction },
  });
  const clear = () => ({ type: CLEAR_SEARCH_TERM });
  const fetchMore = (resultsCount?: number) => ({
    type: FETCH_MORE_SEARCH_RESULTS,
    payload: { hitsPerPage: resultsCount },
  });
  const refetchCurrentPage = () => ({ type: REFETCH_SEARCH_RESULTS_PAGE });

  let fetchEpic: Epic<ReduxAction, { type: string }, AppState, any> = (
    action$: Observable<ReduxAction>,
    state$: StateObservable<AppState>,
  ) =>
    action$.pipe(
      ofType(SET_SEARCH_TERM, SET_SEARCH_SORTING),
      debounceTime(opts.debounce ? 100 : 0),
      switchMap((action) =>
        iif(
          () => action.payload.term === '',
          of(action).pipe(map(clear)),
          fetchSearchResultsForReduxStateObservable(action, state$, { searchType }).pipe(
            map(searchActions['fetchSucceeded']),
            // Cancel if the search is cleared. Also cancel People view loading if another People view load is requested.
            // This fixes a race condition where the default People view fetch (sort by last name) would be loaded AFTER
            // a People view fetch that was triggered by the local store being loaded with a different sort (detailed below)
            takeUntil(
              action$.pipe(ofType(CLEAR_SEARCH_TERM, 'FETCH__ALL_PEOPLE_SEARCH_RESULTS_REQUESTED')),
            ),
            startWith(searchActions['fetchRequested']({ hitsPerPage: action.payload.hitsPerPage })),
            catchError((err) => of(searchActions['fetchFailed'](err))),
          ),
        ),
      ),
    );

  if (searchType == 'allPeopleSearch') {
    const fetchOnLocalStoreLoadEpic = (
      action$: Observable<ReduxAction>,
      state$: StateObservable<AppState>,
    ) =>
      action$.pipe(
        ofType('LOAD_LOCAL_STORE_SUCCEEDED'),
        mergeMap(() => {
          if (
            state$.value.ui.location.pathname != '/people' ||
            (state$.value.data.allPeopleSearch.sorting.name == 'lastName' &&
              state$.value.data.allPeopleSearch.sorting.direction == 'asc')
          ) {
            return of({ type: 'NOOP' });
          }

          return fetchSearchResultsForReduxStateObservable({}, state$, { searchType }).pipe(
            map(searchActions['fetchSucceeded']),
            startWith(searchActions['fetchRequested']()),
            catchError((err) => of(searchActions['fetchFailed'](err))),
          );
        }),
      );

    fetchEpic = combineEpics(fetchEpic, fetchOnLocalStoreLoadEpic);
  }

  const fetchMoreEpic = (action$: Observable<ReduxAction>, state$: StateObservable<AppState>) =>
    action$.pipe(
      ofType(FETCH_MORE_SEARCH_RESULTS, REFETCH_SEARCH_RESULTS_PAGE),
      mergeMap((action) => {
        const currentPage = state$.value.data[searchType].results.page;
        return fetchSearchResultsForReduxStateObservable(action, state$, {
          page: action.type == REFETCH_SEARCH_RESULTS_PAGE ? currentPage : currentPage + 1,
          searchType,
        }).pipe(
          map(searchActions['fetchSucceeded']),
          startWith(searchActions['fetchRequested']()),
          catchError((err) => of(searchActions['fetchFailed'](err))),
        );
      }),
    );

  return [
    {
      [SET_SEARCH_TERM]: SET_SEARCH_TERM,
      [CLEAR_SEARCH_TERM]: CLEAR_SEARCH_TERM,
      [FETCH_MORE_SEARCH_RESULTS]: FETCH_MORE_SEARCH_RESULTS,
      [SET_SEARCH_SORTING]: SET_SEARCH_SORTING,
    },
    { setTerm, clear, fetchMore, sort, refetchCurrentPage },
    fetchEpic,
    fetchMoreEpic,
  ];
}

/* Epic actions */
const searchActions = requestActions('SEARCH_RESULTS', ['fetch']);
/* Custom action types */
const SET_SEARCH_TERM = 'SET_SEARCH_TERM';
const CLEAR_SEARCH_TERM = 'CLEAR_SEARCH_TERM';
const SAVE_SEARCH_TERM = 'SAVE_SEARCH_TERM';
const REMOVE_SEARCH_TERM = 'REMOVE_SEARCH_TERM';
const CLEAR_SEARCH_HISTORY = 'CLEAR_SEARCH_HISTORY';
const SET_SEARCH_SORTING = 'SET_SEARCH_SORTING';
const FETCH_MORE_SEARCH_RESULTS = 'FETCH_MORE_SEARCH_RESULTS';
const FETCH_ALL_SEARCH_RESULTS = 'FETCH_ALL_SEARCH_RESULTS';

export const [
  adHocSearchActionTypes,
  adHocSearchActions,
  fetchAdHocSearchResultsEpic,
  fetchMoreAdHocSearchResultsEpic,
] = createSearchActions('adHocSearch');

export const [mentionSearchActionTypes, mentionSearchActions, fetchMentionSearchResultsEpic] =
  createSearchActions('mentionSearch');

export const [
  allPeopleSearchActionTypes,
  allPeopleSearchActions,
  fetchAllPeopleSearchResultsEpic,
  fetchMoreAllPeopleSearchResultsEpic,
] = createSearchActions('allPeopleSearch', { debounce: false });

export const { sort: sortAllPeopleSearch } = allPeopleSearchActions;

export const actionTypes = {
  SET_SEARCH_TERM,
  CLEAR_SEARCH_TERM,
  SAVE_SEARCH_TERM,
  REMOVE_SEARCH_TERM,
  CLEAR_SEARCH_HISTORY,
  SET_SEARCH_SORTING,
  FETCH_MORE_SEARCH_RESULTS,
  FETCH_ALL_SEARCH_RESULTS,
  ...adHocSearchActionTypes,
  ...mentionSearchActionTypes,
  ...allPeopleSearchActionTypes,
};

/* Component actions */
export const setSearchTerm = (term: string, updateRecent: boolean = false) => ({
  type: SET_SEARCH_TERM,
  payload: { term, updateRecent },
});

export const saveSearchTerm = (term: string) => ({
  type: SAVE_SEARCH_TERM,
  payload: { term },
});

export const removeSearchTerm = (term: string) => ({
  type: REMOVE_SEARCH_TERM,
  payload: { term },
});

export const sortSearch = (type: SearchSortBy, direction: SearchSortDirection) => ({
  type: SET_SEARCH_SORTING,
  payload: { type, direction },
});

export const clearSearchHistory = () => ({ type: CLEAR_SEARCH_HISTORY });
export const fetchMoreSearchResults = () => ({ type: FETCH_MORE_SEARCH_RESULTS });
export const clearSearch = () => ({ type: CLEAR_SEARCH_TERM });
export const fetchSearchTips = () => ({ type: 'FETCH_SEARCH_TIPS' });

/* Selectors */
export const selectSearch = (state: AppState) => state.data.search;
export const selectSearchSort =
  (searchType: SearchType, resourceID?: ID) =>
  (state: AppState): SearchSort => {
    let sort: SearchSort = {};

    if (searchType === 'groupSearch') {
      sort = selectGroup(resourceID)(state).sorting;
    } else {
      sort = state.data[searchType].sorting;
    }

    return !!sort.name ? sort : applySortStateToRequestBody({}, 'closeness');
  };
export const selectAdHocSearch =
  (searchType: SearchType) =>
  (state: AppState): SearchState =>
    state.data[searchType];

export const selectSearchTipsFromAPI = (state: AppState) => {
  return {
    interest: state.data.search.tips.interest,
    location: state.data.search.tips.location,
    org: state.data.search.tips.org,
  };
};

export const selectSearchTipContacts = (state: AppState) => {
  const allContacts = selectAllContacts(state);
  return allContacts.slice(0, 5).valueSeq().toArray();
};

export const selectIsUpcomingBirthdaysSearch = (state: AppState) => {
  return /^upcoming birthdays?$/.test(selectSearch(state).term.toLowerCase());
};

export const defaultDirectionForSort = (
  sortBy: SearchSortBy,
): [SearchSortByField, SearchSortDirection] | [] => {
  switch (sortBy) {
    case 'firstName':
      return ['firstName', 'asc'];
    case 'lastName':
      return ['lastName', 'asc'];
    case 'firstInteraction':
      return ['firstInteractionDate', 'asc'];
    case 'lastInteraction':
      return ['lastInteractionDate', 'desc'];
    case 'created':
      return ['created', 'desc'];
    case 'closeness':
      return ['score', 'desc'];
    default:
      return [];
  }
};

export const applySortStateToRequestBody = (
  body: any,
  sortBy: SearchSortBy = 'closeness',
  direction?: SearchSortDirection,
) => {
  const [sort_by, defaultDirection] = defaultDirectionForSort(sortBy);
  const isDefaultSort = sortBy === 'closeness' && (direction || defaultDirection) === 'desc';

  if (sort_by && !isDefaultSort) {
    return { ...body, sort_by, sort_direction: direction || defaultDirection };
  }

  return body;
};

export const applySortStateToObj = (
  obj: any,
  sortBy: SearchSortBy = 'closeness',
  direction?: SearchSortDirection,
) => {
  const [sort_by, defaultDirection] = defaultDirectionForSort(sortBy);

  if (sort_by) {
    return { ...obj, name: sort_by, direction: direction || defaultDirection };
  }

  return obj;
};

/* Helpers */
export const fetchSearchResultsObservable = (
  user: UserSession,
  term: string,
  hitsPerPage: number,
  excludeRestrictedContacts?: boolean,
  page: number = 1,
  searchType: SearchType = 'adHocSearch',
  searchAttributes?: string[],
  sortBy?: SearchSortBy,
  sortByDirection?: SearchSortDirection,
): Observable<AjaxResponse<any>> => {
  let body: any = applySortStateToRequestBody(
    {
      term: term,
      limit: hitsPerPage,
      page,
    },
    sortBy,
    sortByDirection,
  );

  if (searchAttributes && searchAttributes.length > 0) {
    body.query_by = searchAttributes.join(',');
  }

  if (
    excludeRestrictedContacts ||
    (!['search', 'allPeopleSearch'].includes(searchType) && user.isOverContactLimit)
  ) {
    body.is_restricted = 'true';
  }

  return searchApiObservable('GET', 'search', { user, query: body });
};

const fetchSearchResultsForReduxStateObservable = (
  action: Partial<ReduxAction>,
  state$: StateObservable<AppState>,
  { page = 1, searchType = 'search' }: { page?: number; searchType?: SearchType },
): Observable<AjaxResponse<any>> => {
  return fetchSearchResultsObservable(
    selectUser(state$.value),
    state$.value.data[searchType].term,
    action.payload?.hitsPerPage || state$.value.data.search.results.hitsPerPage,
    undefined,
    page,
    searchType,
    action.payload?.searchAttributes,
    action.payload?.useMainSearchSorting
      ? state$.value.data.search.sorting.name
      : state$.value.data[searchType].sorting.name,
    action.payload?.useMainSearchSorting
      ? state$.value.data.search.sorting.direction
      : state$.value.data[searchType].sorting.direction,
  );
};

/* Epics */
// Only subscribe to output events of the last search input event (switchMap). If the
// last event cleared the search input, emit a clearSearch action. Otherwise, fetch
// results and then maintain subscription unless search is cleared (whether by btn click
// or navigation -- clearing via setting the search term is already handled here).
const fetchSearchResultsEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType(SET_SEARCH_TERM, SET_SEARCH_SORTING),
    debounceTime(100),
    switchMap((action) =>
      iif(
        () => action.payload.term === '',
        of(action).pipe(map(clearSearch)),
        fetchSearchResultsForReduxStateObservable(action, state$, {}).pipe(
          map((results) =>
            searchActions['fetchSucceeded']({ ...results, searchTerm: action.payload.term }),
          ),
          // cancel if the search is cleared
          takeUntil(action$.pipe(ofType(CLEAR_SEARCH_TERM))),
          startWith(searchActions['fetchRequested']()),
          catchError((err) => of(searchActions['fetchFailed'](err))),
        ),
      ),
    ),
  );

export const fetchMoreSearchResultsEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType(FETCH_MORE_SEARCH_RESULTS),
    mergeMap((action) =>
      fetchSearchResultsForReduxStateObservable(action, state$, {
        page: state$.value.data.search.results.page + 1,
      }).pipe(
        map(searchActions['fetchSucceeded']),
        // cancel this request once a user updates or clears the search.
        takeUntil(action$.pipe(ofType(SET_SEARCH_TERM, CLEAR_SEARCH_TERM))),
        startWith(searchActions['fetchRequested']()),
        catchError((err) => of(searchActions['fetchFailed'](err))),
      ),
    ),
  );

const searchTipActions = requestActions('SEARCH_TIPS', ['fetch']);

const searchTipsEpic = (action$: Observable<ReduxAction>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType('FETCH_SEARCH_TIPS'),
    first(), // only fetch custom tips once per session
    mergeMap(() =>
      searchApiObservable('GET', 'search/tips', {
        user: selectUser(state$.value),
      }).pipe(
        map(({ response }) => searchTipActions['fetchSucceeded']({ response })),
        catchError((err) => of(searchTipActions['fetchFailed'](err))),
      ),
    ),
  );

export const searchEpic = combineEpics(
  fetchSearchResultsEpic,
  fetchAdHocSearchResultsEpic,
  fetchMoreAdHocSearchResultsEpic,
  fetchMentionSearchResultsEpic,
  fetchMoreSearchResultsEpic,
  fetchAllPeopleSearchResultsEpic,
  fetchMoreAllPeopleSearchResultsEpic,
  searchTipsEpic,
);
